import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

type PrimeShades = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
};

const SHADE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

function shadeKey(semantic: string, shade: number): string {
  return `--b-${semantic}-${shade}`;
}

function createShades(semantic: string): PrimeShades {
  return SHADE_KEYS.reduce((acc: PrimeShades, shade) => {
    acc[shade] = `var(${shadeKey(semantic, shade)})`;
    return acc;
  }, {} as PrimeShades);
}

const BrandablePreset = definePreset(Aura, {
  options: {
    darkModeSelector: false
  },

  primitive: {
    branding: {
      primary: {
        fill: 'var(--b-ci-fill)',
        contrast: 'var(--b-ci-text)'
      },
      text: 'var(--b-ci-content-text)',
      highlight: 'var(--b-ci-accent)',
    },

    bulk: {
      background: 'var(--b-background)',
    }
  },

  semantic: {
    primary: createShades('primary'),

    colorScheme: {
      light: {
        surface: createShades('surface'),
        content: {
          background: '{bulk.background}'
        },
      }
    },
  },

  components: {
    // Style the megamenu from the branding primitives. This is against the PrimeNG guidelines which state
    // that we should rather be using semantic elements here.
    megamenu: {
      root: {
        background: '{branding.primary.fill}',
        color: '{branding.primary.contrast}',
        borderRadius: '{border.radius.none}',
        borderColor: '{branding.primary.fill}',
      },
      baseItem: {
        borderRadius: '{border.radius.xs}',
      },
      item: {
        color: '{branding.primary.contrast}',
        borderRadius: '{border.radius.xs}',
        focusBackground: '{bulk.background}',
        focusColor: '{branding.text}',
        activeBackground: '{bulk.background}',
        activeColor: '{branding.text}',
      },
      submenuIcon: {
        color: '{branding.highlight}',
        focusColor: '{branding.text}',
        activeColor: '{branding.text}',
      },
      mobileButton: {
        color: '{branding.highlight}',
        hoverColor: '{branding.text}',
        hoverBackground: '{branding.primary.contrast}'
      },
      overlay: {
        borderRadius: '{border.radius.none}',
        borderColor: '{branding.highlight}',
        background: '{branding.primary.fill}',
        color: '{branding.primary.contrast}',
      },
      submenuLabel: {
        color: '{branding.highlight}',
      }
    },
    toolbar: {
      root: {
        borderRadius: '{border.radius.none}',
        background: '{branding.primary.fill}',
        color: '{branding.primary.contrast}',
      }
    }
  }
});

export { BrandablePreset, SHADE_KEYS, shadeKey };
